// VendorBillList.jsx
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import T from '../../components/common/T';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axiosConfig';
import DataTable from "../../components/common/DataTables.jsx";
import TableActionButtons from "../../components/common/TableActionButtons";
import { TopPrimaryButton } from "../../components/common/StandardButtons";
import Price from "../../components/common/Price";
import PageLayout, { PageHeader, StatsGrid, Toolbar, FilterPanel, TableCard } from "../../components/common/PageLayout";
import useModuleFilters from "../../hooks/useModuleFilters";
import {
  DocumentTextIcon, CheckCircleIcon, ExclamationTriangleIcon, PaperAirplaneIcon
} from "@heroicons/react/24/outline";
import VendorBillDetailsModal from './VendorBillDetailsModal';

export default function VendorBillList() {
  const navigate = useNavigate();
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateSort, setDateSort] = useState('');
  const [selectedBill, setSelectedBill] = useState(null);

  const {
    showFilter,
    toggleFilters,
    filterOptions,
    loadingFilters,
  } = useModuleFilters("vendor-bills", {
    statuses: [],
    projects: [],
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/accounting/bills');
      if (Array.isArray(res.data)) setBills(res.data);
    } catch {
      console.error("Failed to load bills");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredBills = useMemo(() => {
    let result = [...bills];
    if (filter && filter !== 'All') {
      result = result.filter(b => (b.status || '').toLowerCase() === filter.toLowerCase());
    }
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      result = result.filter(b =>
        (b.bill_number || '').toLowerCase().includes(s) ||
        (b.vendor_name || '').toLowerCase().includes(s) ||
        (b.project_name || '').toLowerCase().includes(s)
      );
    }
    if (dateSort === 'newest') {
      result.sort((a, b) => new Date(b.bill_date || 0) - new Date(a.bill_date || 0));
    } else if (dateSort === 'oldest') {
      result.sort((a, b) => new Date(a.bill_date || 0) - new Date(b.bill_date || 0));
    }
    return result;
  }, [bills, filter, searchTerm, dateSort]);

  // ── Stats from ALL bills ─────────────────────────────────────────────────
  const stats = useMemo(() => [
    {
      label: "Total Bills",
      value: bills.length,
      icon: DocumentTextIcon,
      variant: "default",
      isActive: filter === 'All' || !filter,
      onClick: () => setFilter('All'),
    },
    {
      label: "Posted",
      value: bills.filter(b => b.status === 'Posted').length,
      icon: PaperAirplaneIcon,
      variant: "blue",
      isActive: filter === 'Posted',
      onClick: () => setFilter(f => f === 'Posted' ? 'All' : 'Posted'),
    },
    {
      label: "Paid",
      value: bills.filter(b => b.status === 'Paid').length,
      icon: CheckCircleIcon,
      variant: "green",
      isActive: filter === 'Paid',
      onClick: () => setFilter(f => f === 'Paid' ? 'All' : 'Paid'),
    },
    {
      label: "Overdue",
      value: bills.filter(b => b.status === 'Overdue').length,
      icon: ExclamationTriangleIcon,
      variant: "red",
      isActive: filter === 'Overdue',
      onClick: () => setFilter(f => f === 'Overdue' ? 'All' : 'Overdue'),
    },
  ], [bills, filter]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Paid': return 'bg-green-50 dark:bg-emerald-950/40 text-green-700 dark:text-emerald-400 border-green-200 dark:border-emerald-800/60';
      case 'Posted': return 'bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800/60';
      case 'Overdue': return 'bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800/60';
      default: return 'bg-gray-50 dark:bg-slate-800 text-gray-700 dark:text-slate-300 border-gray-200 dark:border-slate-700';
    }
  };

  const columns = [
    { header: "Bill #", accessor: "bill_number", sortable: true, render: (b) => <span className="font-bold text-red-600 dark:text-rose-400">{b.bill_number}</span> },
    { header: "Vendor", accessor: "vendor_name", sortable: true, render: (b) => <span className="font-medium text-gray-800 dark:text-white">{b.vendor_name || b.vendor_id}</span> },
    { header: "Project", accessor: "project_name", sortable: true, render: (b) => <span className="text-gray-700 dark:text-slate-300">{b.project_name || "-"}</span> },
    { header: "Bill Date", accessor: "bill_date", sortable: true, render: (b) => <span className="text-gray-600 dark:text-slate-400">{b.bill_date ? new Date(b.bill_date).toLocaleDateString() : '-'}</span> },
    { header: "Due Date", accessor: "due_date", sortable: true, render: (b) => <span className="text-gray-600 dark:text-slate-400">{b.due_date ? new Date(b.due_date).toLocaleDateString() : '-'}</span> },
    { header: "Amount", accessor: "total_amount", align: "right", sortable: true, render: (b) => <span className="font-bold text-gray-900 dark:text-white"><Price amount={Number(b.total_amount || 0)} /></span> },
    {
      header: "Status", accessor: "status", align: "center", sortable: true,
      render: (b) => (
        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide border ${getStatusColor(b.status)}`}>
          {b.status}
        </span>
      )
    },
    {
      header: "Action", align: "right",
      render: (b) => (
        <TableActionButtons
          onView={(e) => { e.stopPropagation(); navigate(`/admin/accounting/bills/${b.id}`); }}
          viewTitle="View Details"
        />
      )
    }
  ];

  return (
    <PageLayout>
      {/* Header bar */}
      <PageHeader
        icon={DocumentTextIcon}
        iconColor="text-[#E8612C]"
        title="Vendor Bills"
        subtitle="Track accounts payable, supplier bills, and payment deadlines"
        onRefresh={loadData}
        loading={loading}
      >
        <TopPrimaryButton
          onClick={() => navigate('/admin/accounting/bills/new')}
          label="Record Bill"
        />
      </PageHeader>

      {/* Mini Dashboard */}
      <StatsGrid stats={stats} cols={4} />

      {/* Unified Search & Filters Toolbar */}
      <Toolbar
        searchTerm={searchTerm}
        onSearch={setSearchTerm}
        searchPlaceholder="Search bills, vendors, projects..."
        tabs={['All', 'Draft', 'Posted', 'Paid', 'Overdue']}
        activeTab={filter}
        onTabChange={setFilter}
        onToggleFilters={toggleFilters}
        showFilters={showFilter}
      />

      {/* Collapsible Filter Panel */}
      <FilterPanel
        isOpen={showFilter}
        loading={loadingFilters}
        onClear={() => { setFilter('All'); setDateSort(''); setSearchTerm(''); }}
      >
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1 flex items-center justify-between">
            <span><T>Status</T></span>
            {loadingFilters && <span className="text-[10px] text-gray-400 animate-pulse font-normal">Loading...</span>}
          </label>
          <select
            value={filter}
            onChange={e => setFilter(e.target.value)}
            disabled={loadingFilters}
            className="w-full border border-gray-300 dark:border-[#30363d] rounded-lg p-2 text-sm bg-white dark:bg-[#21262d] text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-[#E8612C]"
          >
            <option value="All"><T>All Statuses</T></option>
            <option value="Draft"><T>Draft</T></option>
            <option value="Posted"><T>Posted</T></option>
            <option value="Paid"><T>Paid</T></option>
            <option value="Overdue"><T>Overdue</T></option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 dark:text-slate-400 mb-1"><T>Sort By Date</T></label>
          <select
            value={dateSort}
            onChange={e => setDateSort(e.target.value)}
            className="w-full border border-gray-300 dark:border-[#30363d] rounded-lg p-2 text-sm bg-white dark:bg-[#21262d] text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-[#E8612C]"
          >
            <option value=""><T>Default</T></option>
            <option value="newest"><T>Newest First</T></option>
            <option value="oldest"><T>Oldest First</T></option>
          </select>
        </div>
      </FilterPanel>

      {/* Table Card */}
      <TableCard>
        <DataTable data={filteredBills} columns={columns} emptyMessage="No bills match current filters." />
      </TableCard>

      {selectedBill && <VendorBillDetailsModal bill={selectedBill} onClose={() => setSelectedBill(null)} />}
    </PageLayout>
  );
}